import { Trans } from 'react-i18next';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { useTheme } from '@mui/material/styles';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import CircledIcon from '../CircledIcon';
import Typography from '../Typography';
import IconProgress from '../../components/IconProgress';
import {
  StyledFileUploadContainer,
  StyledRemoveButton,
  StyledFlexContainer,
} from './styles';

export interface FileUploadProps extends Omit<DropzoneOptions, 'multiple'> {
  fileName?: string;
  uploaded?: boolean;
  onRemove?: () => void;
  progress?: number;
}

export default function FileUpload(props: FileUploadProps) {
  const { uploaded = false, progress, fileName = '', onRemove } = props;

  const theme = useTheme();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...props,
    multiple: false,
    disabled: uploaded,
  });

  const handleRemoveFile = () => {
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <StyledFileUploadContainer
      {...getRootProps()}
      uploaded={uploaded}
      dragActive={isDragActive}
    >
      <input {...getInputProps()} />
      {uploaded && (
        <>
          <StyledFlexContainer>
            {progress === 100 || !progress ? (
              <CircledIcon
                size="small"
                color={theme.palette.brandPrimary.mainCustom}
                backgroundRGBColor={theme.palette.neutral.RGB100}
              >
                <DescriptionIcon />
              </CircledIcon>
            ) : (
              <IconProgress
                value={progress}
                color={theme.palette.brandPrimary.mainCustom}
                backgroundRGBColor={theme.palette.neutral.RGB100}
              >
                <DescriptionIcon />
              </IconProgress>
            )}
            {fileName && (
              <Typography color={theme.palette.neutral[800]}>
                {fileName}
              </Typography>
            )}
          </StyledFlexContainer>
          <StyledRemoveButton onClick={handleRemoveFile}>
            <ClearSharpIcon />
          </StyledRemoveButton>
        </>
      )}
      {!uploaded && (
        <StyledFlexContainer>
          <CircledIcon
            size="small"
            color={theme.palette.brandPrimary.mainCustom}
            backgroundRGBColor={theme.palette.neutral.RGB100}
          >
            <FileUploadIcon />
          </CircledIcon>

          <Trans
            i18nKey="site.pricing.form-elements.file-upload.label"
            components={{
              1: (
                <Typography
                  variant="smallText"
                  component="span"
                  color={theme.palette.neutral[700]}
                />
              ),
              2: (
                <Typography
                  variant="smallText"
                  component="span"
                  color={theme.palette.brandPrimary.mainCustom}
                  sx={{ fontWeight: 700 }}
                />
              ),
            }}
          />
        </StyledFlexContainer>
      )}
    </StyledFileUploadContainer>
  );
}
